import { useEffect, useRef } from 'react';
import { useAuth } from '../auth';
import './Landing.css';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void;
          renderButton: (el: HTMLElement, config: Record<string, unknown>) => void;
          prompt: () => void;
        };
      };
    };
  }
}

import { BRANDING } from '@flux/shared';

const GOOGLE_CLIENT_ID = BRANDING.GOOGLE_CLIENT_ID;

export function Landing() {
  const { user, loading, login, logout } = useAuth();
  const btnRef = useRef<HTMLDivElement>(null);

  // Initialize GIS when user is not logged in
  useEffect(() => {
    if (user || loading) return;

    const initGis = () => {
      if (!window.google?.accounts?.id || !btnRef.current) return;
      if (!GOOGLE_CLIENT_ID) {
        console.warn('VITE_GOOGLE_CLIENT_ID not set — Google Sign-In disabled');
        return;
      }

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response: { credential: string }) => {
          login(response.credential).catch((err) => {
            alert(`Erro de autenticação: ${err.message}`);
          });
        },
        auto_select: true,       // Auto-select for returning users
        itp_support: true,
      });

      window.google.accounts.id.renderButton(btnRef.current, {
        type: 'standard',
        theme: 'filled_black',
        size: 'large',
        text: 'signin_with',
        shape: 'pill',
        width: 300,
        locale: 'pt-BR',
      });

      // Also try One Tap
      window.google.accounts.id.prompt();
    };

    // GIS script may not be loaded yet
    if (window.google?.accounts?.id) {
      initGis();
    } else {
      // Wait for script to load
      const timer = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(timer);
          initGis();
        }
      }, 200);
      return () => clearInterval(timer);
    }
  }, [user, loading, login]);

  return (
    <div className="landing">
      {/* Logo */}
      <div className="landing__logo">
        <div className="landing__logo-icon">↻</div>
        <div className="landing__logo-text">FLUX</div>
        <div className="landing__subtitle">itaueira — Curadoria de Contexto</div>
      </div>

      {/* Card */}
      <div className="landing__card">
        {loading ? (
          <div className="landing__loading">
            <div className="landing__spinner" />
            Autenticando...
          </div>
        ) : user ? (
          /* Logged in — Menu */
          <div className="landing__menu animate-fade-in">
            <div className="landing__user-info">
              <div className="landing__avatar">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="landing__user-name">{user.name}</div>
                <div className="landing__user-email">{user.email}</div>
              </div>
              <button className="landing__logout" onClick={logout}>
                Sair
              </button>
            </div>

            <a href="/studio" className="landing__option" id="studio-link">
              <div className="landing__option-icon">🎨</div>
              <div className="landing__option-text">
                <h3>FLUX Studio</h3>
                <p>Editor de Skills — crie, edite e gerencie</p>
              </div>
              <span className="landing__option-arrow">→</span>
            </a>

            <a href="/ops" className="landing__option" id="ops-link">
              <div className="landing__option-icon">📊</div>
              <div className="landing__option-text">
                <h3>Operations</h3>
                <p>Monitoramento, investigação e auditoria</p>
              </div>
              <span className="landing__option-arrow">→</span>
            </a>
          </div>
        ) : (
          /* Not logged in — Google Sign-In */
          <div className="animate-fade-in">
            <div className="landing__card-title">Entrar</div>
            <div
              ref={btnRef}
              id="google-login-btn"
              className="landing__gis-container"
            />
            {!GOOGLE_CLIENT_ID && (
              <p className="landing__hint">
                ⚠ VITE_GOOGLE_CLIENT_ID não configurado
              </p>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="landing__footer">
        FLUX v10.0 — itaueira
      </div>
    </div>
  );
}
